/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / ORDERS-CARDS — Kanban-Karten-Render (pure HTML)
   =============================================================================
   S201 REF-7e: aus orders.js extrahiert (Null Verhaltensänderung). Reine
   HTML-Renderer der Board-Karten:
     · renderOrderCard (Operativ) · renderOrderCardCashout (Kassieren, Probesaft-
       Geste) · renderOrderCardCompact (Erledigt) · renderCarryOverCard (Überfällig)
     · renderOrderItemsBlock (Positions-Vorschau) · renderStatusActions (nächster Schritt).
   Inline-onclick-Strings verweisen auf window-gebundene Handler (orderCheckout,
   orderProbesaft, orderMarkSeen, orderSetStatus, orderCancel, openOrderDetail,
   openCorrectionDialog, switchView) — hier NUR als String, gebunden in ihren
   jeweiligen Modulen. `_probesaftAck` (Set) kommt aus orders-actions.js (gleiche
   Instanz → Ack überlebt Realtime). Importiert KEINEN Orchestrator (acyclisch).
============================================================================ */

import {
  htmlEscape, fmtEUR, shortTime, formatMinutes,
  normalizePreorderItems, snapshotHasRenderableItems, itemsToPreorderSnapshot,
  preorderSpecialRequests
} from '../../utils/helpers.js';
import { minutesUntilPickup, STATUS_LABEL, STATUS_BADGE_CLASS } from './orders-data.js';
import { _probesaftAck } from './orders-actions.js';
import { itemsFor, getCachedItems, orderChannels } from '../../core/order-items-cache.js';
import { renderOperatorBadges } from './operator-ui.js';
import { isTableSelfOrder } from '../../core/order-sources.js';
// S248 — was ist an dieser Rechnung schon bezahlt? (eine Quelle, ein Ladeweg)
import { partialPayFor } from './order-receipt-summary.js';
// S253 — Küchen-Hinweise der Gäste am Tisch (phase_185). Auf der Karte stehen
// ALLE Hinweise der Belegung: der Service bedient den Tisch, nicht die Runde.
import { guestNotes, notesForOccupancy, renderGuestNotesHtml } from './table-guest-notes.js';

// -----------------------------------------------------------------------------
// Order-Karten
// -----------------------------------------------------------------------------

export function renderOrderCard(o) {
  const id        = o.id;
  const num       = o.orderNumber || o.order_number || '—';
  const name      = o.customerName || o.customer_name || '—';
  const phone     = o.customerPhone || o.customer_phone || '';
  const pickup    = shortTime(o.pickupTime || o.pickup_time);
  const total     = (o.totalGrossCents != null ? o.totalGrossCents : o.total_gross_cents) || 0;
  const mins      = minutesUntilPickup(o);
  const isPaid    = (o.paymentStatus || o.payment_status) === 'paid';
  const paymentMethod = o.paymentMethod || o.payment_method;
  const invoiceNo = o.invoiceNumber || o.invoice_number;
  const status    = o.status;
  const isFresh   = !o._seen && status === 'pending';
  // S234 T4 — „💳 Online bezahlt"-Badge, damit das Personal stripe-Orders nicht
  // versehentlich doppelt kassiert (die Kassieren-Spalte filtert paid ohnehin raus).
  const stripeBadge = (paymentMethod === 'stripe' && isPaid)
    ? `<span class="order-svc-badge" style="background:#e6f0ff;color:#1c4e80;" title="Online via Stripe bezahlt">💳 Online bezahlt</span>`
    : '';

  // S62 HF8 — 3-Wege-Service-Type-Badge (dine-in / delivery / takeaway).
  const svcType = o.serviceType || 'takeaway';
  const isDineIn   = svcType === 'dine-in';
  const isDelivery = svcType === 'delivery';
  const tableNameForBadge = (() => {
    if (!isDineIn || !o.tableId) return '';
    const state = (window.App && window.App.storage && window.App.storage._getInternalState()) || { tables: [] };
    const t = (state.tables || []).find(x => x.id === o.tableId);
    return t ? (t.name || o.tableId) : o.tableId;
  })();
  const deliveryAddrShort = (() => {
    if (!isDelivery || !o.deliveryAddress) return '';
    const a = o.deliveryAddress;
    const street = a.street || '';
    const plz    = a.plz    || '';
    return `${street}${street && plz ? ' · ' : ''}${plz}`.trim();
  })();
  const orderSvcBadge = (isDineIn
    ? `<span class="order-svc-badge dine-in" title="Hier Essen am Tisch">🍽️ ${htmlEscape(tableNameForBadge)}</span>`
    : isDelivery
      ? `<span class="order-svc-badge delivery" title="Lieferung: ${htmlEscape(deliveryAddrShort)}">🛵 ${htmlEscape(deliveryAddrShort || 'Lieferung')}</span>`
      : `<span class="order-svc-badge takeaway" title="Takeaway-Bestellung">🥡</span>`)
    // S112 — Reservierungs-Order sichtbar kennzeichnen.
    + (o.reservationId ? `<span class="order-svc-badge reservation" title="Gehört zu einer Reservierung">📅 Reservierung</span>` : '')
    // S244 Cut B — die Gäste haben selbst am Tisch getippt. Fürs Personal ein
    // relevanter Unterschied: bei dieser Bestellung hat niemand nachgefragt,
    // ob wirklich „ohne Zwiebeln" gemeint war.
    + (isTableSelfOrder(o) ? `<span class="order-svc-badge selforder" title="Gast hat selbst am Tisch bestellt">📱 Tisch</span>` : '');

  const timePill = pickup
    ? `<div class="order-time-pill ${mins != null && mins <= 15 ? 'urgent' : ''}">
         <div class="order-time-pill-time">${pickup}</div>
         <div class="order-time-pill-rel">${formatMinutes(mins)}</div>
       </div>`
    : `<div class="order-time-pill"><div class="order-time-pill-time">—</div></div>`;

  const itemsHtml = renderOrderItemsBlock(o);

  // Status-Action-Reihe (Statusübergänge)
  const actionRow = renderStatusActions(o);

  // Bezahl-Buttons (nur wenn nicht bezahlt UND Status ≥ confirmed).
  const canPay = !isPaid && !isDelivery && ['confirmed', 'preparing', 'ready', 'picked_up'].includes(status);
  const deliveryStatusLabel = {
    pending:          '📥 Neu — wartet auf Zuweisung',
    assigned:         '🧑‍🍳 Fahrer zugewiesen',
    out_for_delivery: '🛵 In Auslieferung',
    delivered:        '✅ Ausgeliefert',
    cancelled:        '✕ Lieferung abgebrochen'
  };
  const payRow = canPay ? `
    <div class="order-card-pay-row">
      ${
        // S179 — Probesaft-Geste auf jeder Operativ-Karte. _probesaftAck ist
        // modul-global (orders-actions) → ein Ack pro Bestellung schaltet
        // Operativ- UND Cashout-Karte frei.
        !_probesaftAck.has(id) ? `
        <button class="oc-pay-btn oc-pay-btn-probesaft" onclick="event.stopPropagation();orderProbesaft('${id}')" title="Frisch gepressten Probesaft als Geste des Hauses ausgeben — schaltet danach Bar/Karte frei">🧃 Probesaft ausgegeben</button>
      ` : `
        <button class="oc-pay-btn oc-pay-btn-cash" onclick="event.stopPropagation();orderCheckout('${id}','cash')">💶 Bar</button>
        <button class="oc-pay-btn oc-pay-btn-card" onclick="event.stopPropagation();orderCheckout('${id}','card')">💳 Karte</button>
      `}
    </div>
  ` : isDelivery && !isPaid ? `
    <div class="order-card-delivery-status">
      <span class="oc-delivery-status-label">${htmlEscape(deliveryStatusLabel[o.deliveryStatus] || deliveryStatusLabel.pending)}</span>
      <button class="btn btn-ghost btn-xs" onclick="event.stopPropagation();switchView('delivery')" title="Im Liefer-Cockpit verwalten">
        🛵 Cockpit →
      </button>
    </div>
  ` : isPaid ? `
    <div class="order-card-paid-info">
      ✓ Bezahlt (${paymentMethod === 'cash' ? 'Bar' : paymentMethod === 'card' ? 'Karte' : paymentMethod === 'stripe' ? '💳 Online' : paymentMethod || '–'})${invoiceNo ? ' · Rg. ' + htmlEscape(String(invoiceNo)) : ''}
    </div>
  ` : '';

  // Phase 10a.2: Storno-Pill bei korrigierten Orders
  const correctedPill = o.correctedByOrderId
    ? `<span class="order-corrected-pill" title="Storniert via Korrekturbeleg">STORNIERT</span>`
    : '';

  // Phase 19 (Session 22) — Sub-Status-Indicator pro Channel im Cockpit.
  const channelDot = (s) => ({ pending: '◯', preparing: '⏳', ready: '✓' })[s || 'pending'] || '?';
  const channelColor = (s) => ({ pending: 'var(--ink-muted)', preparing: '#f5b73a', ready: '#4caf50' })[s || 'pending'] || 'inherit';
  const deliveryDot = (s) => ({
    pending: '◯', assigned: '🧑‍🍳', out_for_delivery: '🛵', delivered: '✅', cancelled: '✕'
  })[s || 'pending'] || '?';
  const deliveryDotColor = (s) => ({
    pending: 'var(--ink-muted)', assigned: '#4a86d4', out_for_delivery: '#d4a82c',
    delivered: '#2cb968', cancelled: 'var(--ink-muted)'
  })[s || 'pending'] || 'inherit';
  const deliveryStatusShort = {
    pending: 'Neu', assigned: 'Zugewiesen', out_for_delivery: 'In Auslieferung',
    delivered: 'Ausgeliefert', cancelled: 'Abgebrochen'
  };
  // S101 — item-aware: nur tatsächlich betroffene Kanäle zeigen. Keine Items
  // bekannt → beide Pills (Status quo).
  const chItems = itemsFor(o);
  const ch = chItems.length ? orderChannels(chItems) : { hasFood: true, hasDrinks: true };
  const subStatusPills = (status === 'pending' || status === 'cancelled' || status === 'picked_up')
    ? ''
    : `<div class="order-card-substatus">
         ${ch.hasFood ? `<span class="oc-chip" data-state="${o.kitchenStatus || 'pending'}" title="Küchen-Status">🍳 ${STATUS_LABEL[o.kitchenStatus] || 'Neu'}</span>` : ''}
         ${ch.hasDrinks ? `<span class="oc-chip" data-state="${o.barStatus || 'pending'}" title="Theken-Status">🥤 ${STATUS_LABEL[o.barStatus] || 'Neu'}</span>` : ''}
         ${isDelivery ? `<span class="oc-chip oc-chip-delivery" data-state="${o.deliveryStatus || 'pending'}" title="Liefer-Status">🛵 ${htmlEscape(deliveryStatusShort[o.deliveryStatus] || 'Neu')}</span>` : ''}
       </div>`;

  // S248 — steht an dieser Rechnung schon etwas? Dann zeigt der Kopf den OFFENEN
  // Rest: der Service liest dort ab, was er kassieren soll. Der Gesamtbetrag
  // bleibt im Detail-Modal sichtbar.
  const partPay = partialPayFor(o);
  const partPayBadge = partPay
    ? `<div class="oc-partpay${partPay.online ? ' is-online' : ''}" title="Gesamt ${htmlEscape(fmtEUR(total / 100))}">${htmlEscape(partPay.text)}</div>`
    : '';
  // S253 — „Nussallergie" gehört auf die Karte, nicht nur ins Detail: wer die
  // Bestellung an den Tisch bringt, liest sie hier.
  const guestNoteHtml = o.occupancyId
    ? renderGuestNotesHtml(notesForOccupancy(guestNotes(), o.occupancyId), { cls: 'oc' })
    : '';

  const pickupPill = pickup
    ? `<span class="oc-pickup-pill ${mins != null && mins <= 15 ? 'urgent' : ''}" title="${pickup}">⏱ ${pickup}${mins != null ? ' · ' + formatMinutes(mins) : ''}</span>`
    : '';

  return `
    <div class="order-card ${isFresh ? 'order-fresh' : ''} oc-status-${status}${o.correctedByOrderId ? ' order-corrected' : ''}" data-id="${id}" onclick="orderMarkSeen('${id}')">
      <div class="order-card-top">
        <div class="order-card-num">${htmlEscape(String(num))}${correctedPill ? ' ' + correctedPill : ''}</div>
        <div class="order-card-total">${fmtEUR((partPay ? partPay.openCents : total) / 100)}</div>
      </div>

      ${partPayBadge}
      ${guestNoteHtml}

      <div class="order-card-badges">
        ${orderSvcBadge}
        ${stripeBadge}
        ${pickupPill}
        ${renderOperatorBadges(o)}
      </div>

      <div class="order-card-customer">
        <strong>${htmlEscape(name)}</strong>
        ${phone ? `<a class="order-card-phone" href="tel:${htmlEscape(phone)}" onclick="event.stopPropagation()">📞 ${htmlEscape(phone)}</a>` : ''}
      </div>

      ${subStatusPills}

      ${itemsHtml ? `<div class="order-card-items-wrap">${itemsHtml}</div>` : ''}

      ${actionRow}
      ${payRow}

      <div class="order-card-footer">
        <button class="btn btn-ghost btn-xs" onclick="event.stopPropagation();openOrderDetail('${id}')">Details</button>
        ${status === 'cancelled'
          ? ''
          : o.correctedByOrderId
            ? `<button class="btn btn-ghost btn-xs" style="color:var(--danger);"
                       onclick="event.stopPropagation();openOrderDetail('${htmlEscape(String(o.correctedByOrderId))}')">Stornobeleg ansehen</button>`
            : o.paymentStatus === 'paid'
              ? `<button class="btn btn-ghost btn-xs" style="color:var(--danger,#b94a48);"
                         onclick="event.stopPropagation();openCorrectionDialog('${id}')">↩ Stornobeleg</button>`
              : `<button class="btn btn-ghost btn-xs" onclick="event.stopPropagation();orderCancel('${id}')">Storno</button>`}
      </div>
    </div>
  `;
}

/** S60 FIX.5 — Kompakte Karte für die „Kassieren"-Spalte. KEIN Timer, KEIN
 *  Items-Block. Der finale Bezahl-Klick stempelt automatisch paid_at (Audit). */
export function renderOrderCardCashout(o) {
  const id        = o.id;
  const num       = o.orderNumber || o.order_number || '—';
  const name      = o.customerName || o.customer_name || '—';
  const phone     = o.customerPhone || o.customer_phone || '';
  const total     = (o.totalGrossCents != null ? o.totalGrossCents : o.total_gross_cents) || 0;

  const svcType = o.serviceType || 'takeaway';
  const isDineIn   = svcType === 'dine-in';
  const isDelivery = svcType === 'delivery';
  const tableNameForBadge = (() => {
    if (!isDineIn || !o.tableId) return '';
    const state = (window.App && window.App.storage && window.App.storage._getInternalState()) || { tables: [] };
    const t = (state.tables || []).find(x => x.id === o.tableId);
    return t ? (t.name || o.tableId) : o.tableId;
  })();
  const deliveryAddrShort = (() => {
    if (!isDelivery || !o.deliveryAddress) return '';
    const a = o.deliveryAddress;
    return `${a.street || ''}${a.street && a.plz ? ' · ' : ''}${a.plz || ''}`.trim();
  })();
  const svcBadge = isDineIn
    ? `<span class="order-svc-badge dine-in" title="Hier Essen am Tisch">🍽️ ${htmlEscape(tableNameForBadge)}</span>`
    : isDelivery
      ? `<span class="order-svc-badge delivery" title="Lieferung: ${htmlEscape(deliveryAddrShort)}">🛵 ${htmlEscape(deliveryAddrShort || 'Lieferung')}</span>`
      : `<span class="order-svc-badge takeaway" title="Takeaway-Bestellung">🥡</span>`;

  // S248 — steht an dieser Rechnung schon etwas? Dann zeigt der Kopf den OFFENEN
  // Rest: der Service liest dort ab, was er kassieren soll. Der Gesamtbetrag
  // bleibt im Detail-Modal sichtbar.
  const partPay = partialPayFor(o);
  const partPayBadge = partPay
    ? `<div class="oc-partpay${partPay.online ? ' is-online' : ''}" title="Gesamt ${htmlEscape(fmtEUR(total / 100))}">${htmlEscape(partPay.text)}</div>`
    : '';
  // S253 — „Nussallergie" gehört auf die Karte, nicht nur ins Detail: wer die
  // Bestellung an den Tisch bringt, liest sie hier.
  const guestNoteHtml = o.occupancyId
    ? renderGuestNotesHtml(notesForOccupancy(guestNotes(), o.occupancyId), { cls: 'oc' })
    : '';

  return `
    <div class="order-card oc-status-cashout" data-id="${id}" onclick="orderMarkSeen('${id}')">
      <div class="order-card-top">
        <div class="order-card-num">${htmlEscape(String(num))}</div>
        <div class="order-card-total">${fmtEUR((partPay ? partPay.openCents : total) / 100)}</div>
      </div>

      ${partPayBadge}
      ${guestNoteHtml}

      <div class="order-card-badges">
        ${svcBadge}
        <span class="oc-cashout-marker">💰 Kassieren</span>
      </div>

      <div class="order-card-customer">
        <strong>${htmlEscape(name)}</strong>
        ${phone ? `<a class="order-card-phone" href="tel:${htmlEscape(phone)}" onclick="event.stopPropagation()">📞 ${htmlEscape(phone)}</a>` : ''}
      </div>

      <div class="order-card-primary-actions">
        ${isDelivery ? `
          <button class="oc-primary-btn oc-primary-btn-delivery" onclick="event.stopPropagation();switchView('delivery')" title="Zahlung wird im Liefer-Cockpit beim „Ausgeliefert + Bezahlt"-Klick abgewickelt">
            🛵 Im Liefer-Cockpit kassieren
          </button>
        ` : (
          // S171 — Probesaft-Geste: erst nach Klick auf „Probesaft" werden Bar/Karte freigeschaltet.
          !_probesaftAck.has(id) ? `
          <button class="oc-primary-btn oc-primary-btn-probesaft" onclick="event.stopPropagation();orderProbesaft('${id}')" title="Frisch gepressten Probesaft als Geste des Hauses ausgeben — schaltet danach Bar/Karte frei">
            🧃 Probesaft ausgegeben
          </button>
        ` : `
          <button class="oc-primary-btn oc-primary-btn-cash" onclick="event.stopPropagation();orderCheckout('${id}','cash')">💶 Bar</button>
          <button class="oc-primary-btn oc-primary-btn-card" onclick="event.stopPropagation();orderCheckout('${id}','card')">💳 Karte</button>
        `)}
      </div>

      <div class="order-card-footer">
        <button class="btn btn-ghost btn-xs" onclick="event.stopPropagation();openOrderDetail('${id}')">Details</button>
        <button class="btn btn-ghost btn-xs" onclick="event.stopPropagation();orderCancel('${id}')">Storno</button>
      </div>
    </div>
  `;
}

/** Kompakte Karte für die „Erledigt heute"-Akkordeon-Liste. */
export function renderOrderCardCompact(o) {
  const num   = o.orderNumber || o.order_number || '—';
  const name  = o.customerName || o.customer_name || '—';
  const pickup= shortTime(o.pickupTime || o.pickup_time);
  const total = (o.totalGrossCents != null ? o.totalGrossCents : o.total_gross_cents) || 0;
  const status= o.status;
  return `
    <div class="order-card-compact status-${status}">
      <div class="order-card-compact-time">${pickup || '—'}</div>
      <div class="order-card-compact-main">
        <div><strong>${htmlEscape(name)}</strong> · ${htmlEscape(String(num))}</div>
        <div class="meta">
          <span class="badge ${STATUS_BADGE_CLASS[status] || ''}">${STATUS_LABEL[status] || status}</span>
          · ${fmtEUR(total / 100)}
        </div>
      </div>
      <button class="btn btn-ghost btn-xs" onclick="openOrderDetail('${o.id}')">Details</button>
    </div>
  `;
}

/** S100-A — Carry-Over-Karte: die volle operative Karte plus Alters-/Datums-Badge. */
export function renderCarryOverCard(o) {
  const date = o.pickupDate || o.pickup_date || '';
  const mins = minutesUntilPickup(o);
  const overdueMin = (mins != null && mins < 0) ? Math.abs(mins) : null;
  const parts = String(date).split('-'); // YYYY-MM-DD
  const dateShort = parts.length === 3 ? `${parts[2]}.${parts[1]}.` : date;
  const badge = `<div class="order-carryover-badge" title="Offene Bestellung von einem früheren Tag — bitte abschließen oder stornieren">⚠️ von ${htmlEscape(dateShort)}${overdueMin != null ? ` · ${overdueMin} Min überfällig` : ''}</div>`;
  return `<div class="order-carryover-item">${badge}${renderOrderCard(o)}</div>`;
}

/** Item-Block mit Anpassungen (gleiches Pattern wie Belegansicht im Warenkorb). */
export function renderOrderItemsBlock(o) {
  // S104-A: Cockpit-erfasste Dine-In-Orders haben `preorder_snapshot = {}` (truthy) —
  // ihre Positionen liegen in order_items. Trägt der echte Snapshot nichts
  // Renderbares, konvertieren wir die gecachten order_items via itemsToPreorderSnapshot.
  const realSnap = o.preorderSnapshot || o.preorder_snapshot;
  let snap = realSnap;
  if (!snapshotHasRenderableItems(realSnap)) {
    const cachedRows = getCachedItems(o.id);
    snap = (Array.isArray(cachedRows) && cachedRows.length)
      ? (itemsToPreorderSnapshot(cachedRows) || realSnap)
      : realSnap;
  }
  const items = normalizePreorderItems(snap);
  if (!items.length) return '';
  const lines = items.map(it => {
    const note = it.note ? `<div class="order-item-note">${htmlEscape(it.note)}</div>` : '';
    return `
      <div class="order-item">
        <div class="order-item-row">
          <span class="order-item-qty">${it.qty}×</span>
          <span class="order-item-name">${htmlEscape(it.displayName)}</span>
          <span class="order-item-price">${fmtEUR(it.lineTotal)}</span>
        </div>
        ${note}
      </div>
    `;
  }).join('');
  const special = preorderSpecialRequests(snap);
  return `
    <div class="order-items">
      ${lines}
      ${special ? `<div class="order-special">⚠️ ${htmlEscape(special)}</div>` : ''}
    </div>
  `;
}

/** Status-Action-Reihe pro Karte (NÄCHSTER Schritt prominent). */
export function renderStatusActions(o) {
  const id = o.id;
  const s  = o.status;
  // Phase 19.2 — Cockpit-Action-Buttons reduziert:
  //   pending → „Annehmen" (confirmed); ready → „Abgeholt"/„No-Show"; confirmed/
  //   preparing: KEINE Master-Buttons (KDS/Bar steuern channel-aware).
  if (s === 'pending') {
    return `
      <div class="order-card-primary-actions">
        <button class="oc-primary-btn" onclick="event.stopPropagation();orderSetStatus('${id}','confirmed')">✓ Annehmen</button>
      </div>`;
  }
  if (s === 'ready') {
    // S99 — Lieferungen terminal NUR im Fahrer-Cockpit; „Abgeholt" hier würde den
    // Master aus ['pending'…'ready'] kippen → Liefer-Cockpit-Gate hängt (Sackgasse).
    const isDelivery = (o.serviceType || 'takeaway') === 'delivery';
    if (isDelivery) {
      return `
      <div class="order-card-primary-actions">
        <button class="oc-primary-btn oc-primary-btn-delivery" onclick="event.stopPropagation();switchView('delivery')" title="Lieferungen werden im Liefer-Cockpit auf „ausgeliefert" gesetzt">🛵 Zum Liefer-Cockpit</button>
      </div>`;
    }
    return `
      <div class="order-card-primary-actions">
        <button class="oc-primary-btn" onclick="event.stopPropagation();orderSetStatus('${id}','picked_up')">✓ Abgeholt</button>
        <button class="oc-primary-btn oc-primary-btn-ghost" onclick="event.stopPropagation();orderSetStatus('${id}','no_show')">No-Show</button>
      </div>`;
  }
  // confirmed/preparing: bewusst keine Action-Buttons hier — KDS/Bar steuern.
  return '';
}
